import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import FieldGuidePage from 'src/components/field-guide/FieldGuidePage';
import ApiClient from 'src/lib/api_client';
import RevalidateSettings from 'src/lib/revalidate_settings';
import { AppRoutes } from 'src/lib/routes';
import { prepareTranslations } from 'src/lib/prepare_translations';
import { Blog, FieldGuide } from 'src/types';
import { NextSeo } from 'next-seo';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';

const CollectionFieldGuideShow: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  fieldGuide,
  blogs,
}) => {
  const addLocaleToUrl = useAddLocaleToUrl();

  return (
    <>
      <NextSeo
        title={fieldGuide.shortDestinationName}
        description={fieldGuide.overviewDescription}
        canonical={addLocaleToUrl(
          AppRoutes.collectionFieldGuideRoute({ id: fieldGuide.fieldGuideableSlug }).toUrl()
        )}
        noindex={true}
        nofollow={true}
        openGraph={{
          url: addLocaleToUrl(
            AppRoutes.collectionFieldGuideRoute({ id: fieldGuide.fieldGuideableSlug }).toUrl()
          ),
          title: fieldGuide.shortDestinationName || '',
          description: fieldGuide.overviewDescription || '',
          images: fieldGuide.image
            ? [
                {
                  url: fieldGuide.image.mdUrl,
                  height: 600,
                  width: 600,
                  alt: fieldGuide.shortDestinationName || '',
                },
              ]
            : undefined,
        }}
      />
      <FieldGuidePage fieldGuide={fieldGuide} blogs={blogs} />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<{ fieldGuide: FieldGuide; blogs: Blog[] }> = async (
  context
) => {
  if (!context.params?.id) {
    return {
      notFound: true,
      revalidate: RevalidateSettings.short,
    };
  }

  const translations = await prepareTranslations(context, []);
  const [fieldGuide] = await ApiClient.getFieldGuide(
    { id: `Collection_${context.params.id}` },
    { 'X-NEXT-LOCALE': translations.locale }
  );

  if ('error' in fieldGuide) {
    return {
      notFound: true,
      revalidate: RevalidateSettings.short,
    };
  }

  const [{ data: blogs = [] }] = await ApiClient.getBlogs(
    {
      linkable_type: 'Collection',
      linkable_id: fieldGuide.fieldGuideableId,
      sort: 'most_popular',
      page: 1,
      per_page: 50,
    },
    { 'X-NEXT-LOCALE': translations.locale }
  );

  return {
    props: {
      fieldGuide,
      blogs,
      ...translations,
    },
    revalidate: RevalidateSettings.medium,
  };
};

export default CollectionFieldGuideShow;
